import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App, Button, Empty, Input, Modal, Popconfirm, Popover, Result, Select, Space, Spin, Table, Tabs, Tag, Tooltip, Typography,
  theme,
} from 'antd';
import {
  CopyOutlined, KeyOutlined, LogoutOutlined, MailOutlined, MobileOutlined, QuestionCircleOutlined, ReloadOutlined,
  SafetyCertificateOutlined, UnlockOutlined, UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';
import '../ortak/nfmodules.css';

/**
 * BT — Giriş Güvenliği (03.11.2026). Kilit kaldırma, günlük SMS kodunu sıfırlama, oturum kapatma, e-postası olmayan
 * kişiye şifre yenileme bağlantısı, olay kaydı. Sunucu: routes/giris_guvenligi.py (yalnız 327 ve 199; 403 → erişim
 * yok ekranı). Sayfa kendiliğinden yenilenmez: periyodik istek oturumları hiç kapanmayan hale getirirdi
 * (services/oturum.py ARKA_PLAN) — "Yenile" düğmesi var.
 */
const { Text, Title } = Typography;

const hata = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : e?.message || 'İşlem başarısız.';
};
const saat = (epoch) => (epoch ? dayjs.unix(epoch).format('HH:mm') : '—');
const tarihSaat = (epoch) => (epoch ? dayjs.unix(epoch).format('DD.MM HH:mm') : '—');
const sure = (sn) => {
  const s = Math.max(0, Math.round(sn));
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  return `${Math.floor(s / 3600)} sa ${Math.floor((s % 3600) / 60)} dk`;
};
const once = (epoch) => {
  const dk = Math.max(0, Math.round((Date.now() / 1000 - epoch) / 60));
  return dk < 1 ? 'az önce' : dk < 60 ? `${dk} dk önce` : `${Math.floor(dk / 60)} sa ${dk % 60} dk önce`;
};
const tarayici = (ua) => {
  if (!ua) return '—';
  const t = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Tarayıcı';
  const s = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS'
    : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return s ? `${t} · ${s}` : t;
};
const ADIM = { sifre: 'Şifre', kod: 'SMS kodu', ip: 'IP adresi' };

const OLAY = {
  giris: ['Giriş', 'success'],
  sifre_hatali: ['Hatalı şifre', 'error'],
  kod_hatali: ['Hatalı kod', 'error'],
  robot_basarisiz: ['Robot doğrulaması başarısız', 'error'],
  sms_kod: ['Kod SMS\'i gönderildi', 'processing'],
  sms_hatasi: ['SMS gönderilemedi', 'error'],
  sms_sinir: ['Günlük SMS sınırı', 'warning'],
  kod_iptal: ['Kod iptal (günlük yanlış sınırı)', 'error'],
  telefon_yok: ['Telefon kaydı yok', 'warning'],
  pasif_hesap: ['Pasif hesap denemesi', 'warning'],
  oturum_bosta: ['Hareketsizlikten kapandı', 'default'],
  cikis: ['Çıkış', 'default'],
  sifirlama_talep: ['Şifre yenileme talebi', 'processing'],
  sifirlama_eposta: ['Yenileme e-postası gitti', 'processing'],
  sifirlama_eposta_hatasi: ['Yenileme e-postası gitmedi', 'error'],
  sifre_degisti: ['Şifre değiştirildi', 'success'],
  sms_sifre_degisti: ['Şifre değişti SMS\'i', 'default'],
  bt_kilit_kaldir: ['BT: kilit kaldırıldı', 'purple'],
  bt_kod_sifirla: ['BT: kod sıfırlandı', 'purple'],
  bt_oturum_kapat: ['BT: oturum kapatıldı', 'purple'],
  bt_sifirlama_baglantisi: ['BT: yenileme bağlantısı', 'purple'],
};
const TALEP_SONUC = { 'hesap-yok': 'hesap yok', pasif: 'pasif hesap', 'eposta-yok': 'e-posta kaydı yok',
  sinir: 'saatlik sınır', gonderiliyor: 'bağlantı gönderildi' };
// services/giris_guvenligi.altcha_dogrula sebepleri
const ROBOT_SEBEP = { cozum: 'çözüm hatalı', 'gorev-yok': 'görev yok ya da kullanılmış', sure: 'süresi dolmuş',
  'yuk-bozuk': 'bozuk yük', 'yuk-yok': 'yük yok', sayac: 'geçersiz sayaç' };

const ayrinti = (o) => [
  o.sonuc && TALEP_SONUC[o.sonuc],
  o.hata && (typeof o.hata === 'number' ? `${o.hata}. hata` : o.hata),
  o.kilit && 'kilitlendi',
  o.adim && ADIM[o.adim],
  o.sebep && `sebep: ${ROBOT_SEBEP[o.sebep] || o.sebep}`,
  o.tel,
  o.netgsm && `NetGSM: ${o.netgsm}`,
  o.kapanan_oturum !== undefined && `${o.kapanan_oturum} oturum kapandı`,
  o.tur && o.hedef && `${ADIM[o.tur] || o.tur}: ${o.hedef}`,
  o.bt && `işlemi yapan: ${o.bt}`,
].filter(Boolean).join(' · ');

function useGgApi() {
  const { getAuthHeaders } = useAuth();
  return useMemo(() => {
    const cfg = () => ({ headers: getAuthHeaders() });
    const root = `${API_BASE_URL}/bt-giris-guvenligi`;
    return {
      get: (yol) => axios.get(`${root}${yol}`, cfg()).then((r) => r.data),
      post: (yol, govde) => axios.post(`${root}${yol}`, govde, cfg()).then((r) => r.data),
    };
  }, [getAuthHeaders]);
}

const KisiAdi = ({ ad, kul, no }) => (
  <span>{ad || <Text type="secondary">Kayıtsız ad</Text>}
    {(kul || no) && <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>{kul || `#${no}`}</Text>}
  </span>
);

export default function GirisGuvenligiPage() {
  const api = useGgApi();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [veri, setVeri] = useState(null);
  const [kisiler, setKisiler] = useState([]);
  const [erisimHatasi, setErisimHatasi] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [secili, setSecili] = useState(null);
  const [baglanti, setBaglanti] = useState(null);
  const [ara, setAra] = useState('');
  const [sekme, setSekme] = useState('kilitler');

  const yukle = useCallback(() => {
    setYukleniyor(true);
    return Promise.all([api.get(''), api.get('/kisiler')])
      .then(([d, k]) => { setVeri(d.data); setKisiler(k.data); })
      .catch((e) => {
        if (e?.response?.status === 403) setErisimHatasi(hata(e));
        else message.error(hata(e));
      })
      .finally(() => setYukleniyor(false));
  }, [api, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const islem = (yol, govde) => api.post(yol, govde)
    .then((d) => { message.success(d.detail || 'Tamamlandı.'); yukle(); return d; })
    .catch((e) => { message.error(hata(e)); });

  const kisi = kisiler.find((k) => k.kullanici_no === secili) || null;
  const olaylar = useMemo(() => {
    const a = ara.trim().toLocaleLowerCase('tr');
    const liste = veri?.olaylar || [];
    if (!a) return liste;
    return liste.filter((o) => [o.ad_soyad, o.kul, o.ip, (OLAY[o.olay] || [o.olay])[0], o.bt]
      .some((v) => String(v || '').toLocaleLowerCase('tr').includes(a)));
  }, [veri, ara]);

  if (erisimHatasi) return <Result status="403" title="Erişim Yok" subTitle={erisimHatasi} />;
  if (!veri) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  const ay = veri.ayarlar;
  const kilitli = veri.kilitler.filter((k) => k.kilit_kalan > 0).length;

  const baglantiUret = () => api.post('/sifirlama-baglantisi', { kullanici_no: secili })
    .then((d) => { setBaglanti(d); yukle(); })
    .catch((e) => message.error(hata(e)));
  const kopyala = () => navigator.clipboard.writeText(baglanti.baglanti)
    .then(() => message.success('Bağlantı kopyalandı.'))
    .catch(() => message.warning('Kopyalanamadı; bağlantıyı seçip elle kopyalayın.'));

  const nasil = (
    <div style={{ maxWidth: 380, fontSize: 13, lineHeight: 1.6 }}>
      <p style={{ marginTop: 0 }}>Şifre doğruysa telefona 6 haneli kod gider; kod o gün 23:59'a kadar geçerlidir.</p>
      <p>Şifre ve kod adımında {ay.robot_esigi} hatadan sonra robot doğrulaması istenir, üst üste {ay.kilit_esigi} hatada
        {' '}{ay.kilit_dakika} dakika beklenir. Günde {ay.gunluk_yanlis} yanlış kodda kod iptal olur, günde en çok
        {' '}{ay.gunluk_gonderim} kod gönderilir.</p>
      <p><b>Kilidi kaldır</b>: beklemeyi ve hata sayaçlarını siler. <b>Kodu sıfırla</b>: bugünkü kodu ve günlük sınırları
        siler; kişi yeniden girişte yeni SMS alır. <b>Oturumları kapat</b>: telefon/bilgisayar kaybında kullanın.</p>
      <p style={{ marginBottom: 0 }}><b>Şifre yenileme bağlantısı</b>: e-postası olmayan kişi için. Bağlantıyı vermeden önce
        kişiyi kayıtlı telefonundan arayıp kimliğini teyit edin; bağlantı 30 dakika ve tek kullanımlıktır.</p>
    </div>
  );

  const kilitSutunlari = [
    { title: 'Kişi / Hedef', key: 'kisi', render: (_, k) => (k.tur === 'ip'
      ? <span><Tag>IP</Tag>{k.hedef}</span>
      : <KisiAdi ad={k.ad_soyad} kul={k.tur === 'sifre' ? k.hedef : null} no={k.kullanici_no} />) },
    { title: 'Adım', dataIndex: 'tur', width: 110, render: (t) => ADIM[t] || t },
    { title: 'Hatalı deneme', dataIndex: 'hata', width: 120, align: 'center' },
    { title: 'Durum', key: 'durum', width: 190, render: (_, k) => (k.kilit_kalan > 0
      ? <Tag color="error">Kilitli · {sure(k.kilit_kalan)}</Tag>
      : <Tag color="warning">Robot doğrulaması istiyor</Tag>) },
    { title: '', key: 'eylem', width: 140, render: (_, k) => (
      <Button size="small" icon={<UnlockOutlined />} onClick={() => islem('/kilit-kaldir', { tur: k.tur, hedef: k.hedef })}>
        Kilidi kaldır
      </Button>) },
  ];
  const oturumSutunlari = [
    { title: 'Kişi', key: 'kisi', render: (_, o) => <KisiAdi ad={o.ad_soyad} kul={o.kul} /> },
    { title: 'IP', dataIndex: 'ip', width: 140 },
    { title: 'Tarayıcı', dataIndex: 'tarayici', width: 150, render: (ua) => <Tooltip title={ua}>{tarayici(ua)}</Tooltip> },
    { title: 'Giriş', dataIndex: 'olusma', width: 110, render: tarihSaat },
    { title: 'Son işlem', dataIndex: 'son', width: 130, render: once },
    { title: 'Kalan', dataIndex: 'kalan', width: 110, render: sure },
    { title: '', key: 'eylem', width: 110, render: (_, o) => (
      <Popconfirm title="Bu oturum kapatılsın mı?" okText="Kapat" cancelText="Vazgeç"
        onConfirm={() => islem('/oturum-kapat', { sid: o.sid })}>
        <Button size="small" danger icon={<LogoutOutlined />}>Kapat</Button>
      </Popconfirm>) },
  ];
  const kodSutunlari = [
    { title: 'Kişi', key: 'kisi', render: (_, k) => <KisiAdi ad={k.ad_soyad} no={k.kullanici_no} /> },
    { title: 'Son gönderim', dataIndex: 'gonderim', width: 130, render: saat },
    { title: 'Gönderim', dataIndex: 'gonderim_sayisi', width: 110, align: 'center', render: (n) => `${n} / ${ay.gunluk_gonderim}` },
    { title: 'Yanlış', dataIndex: 'yanlis', width: 100, align: 'center', render: (n) => `${n} / ${ay.gunluk_yanlis}` },
    { title: 'Kod kilidi', dataIndex: 'kilit_kalan', width: 120, render: (s) => (s > 0 ? <Tag color="error">{sure(s)}</Tag> : '—') },
    { title: '', key: 'eylem', width: 140, render: (_, k) => (
      <Button size="small" icon={<KeyOutlined />} onClick={() => islem('/kod-sifirla', { kullanici_no: k.kullanici_no })}>
        Kodu sıfırla
      </Button>) },
  ];
  const olaySutunlari = [
    { title: 'Zaman', dataIndex: 't', width: 130, render: (t) => dayjs(t).format('DD.MM HH:mm:ss') },
    { title: 'Olay', dataIndex: 'olay', width: 230, render: (o) => {
      const [ad, renk] = OLAY[o] || [o, 'default'];
      return <Tag color={renk}>{ad}</Tag>;
    } },
    { title: 'Kişi', key: 'kisi', width: 200, render: (_, o) => (o.ad_soyad || o.kul
      ? <KisiAdi ad={o.ad_soyad} kul={o.kul} /> : <Text type="secondary">—</Text>) },
    { title: 'IP', dataIndex: 'ip', width: 130 },
    { title: 'Ayrıntı', key: 'ayrinti', render: (_, o) => <Text type="secondary" style={{ fontSize: 13 }}>{ayrinti(o)}</Text> },
  ];

  const ozet = [
    { ad: 'Kilitli', sayi: kilitli, ton: kilitli ? token.colorError : token.colorTextTertiary },
    { ad: 'Robot doğrulama eşiğinde', sayi: veri.kilitler.length - kilitli, ton: token.colorWarning },
    { ad: 'Bugün kod alan', sayi: veri.kodlar.length, ton: token.colorPrimary },
    { ad: 'Açık oturum', sayi: veri.oturumlar.length, ton: token.colorSuccess },
  ];

  return (
    <div className="nf-module gg-sayfa" style={{
      '--gg-yuzey': token.colorBgContainer, '--gg-kenar': token.colorBorderSecondary, '--gg-ikincil': token.colorTextSecondary,
      '--gg-dolgu': token.colorFillQuaternary }}>
      <style>{`
        /* Koyu tema: DarkMode.css tabloyu/etiketleri !important ile boyuyor (DomainHostingPage ile aynı düzeltme) */
        body[data-theme='dark'] .gg-sayfa .ant-table-tbody > tr:nth-child(even) > td { background: rgba(255, 255, 255, 0.025) !important; }
        body[data-theme='dark'] .gg-sayfa .ant-table-tbody > tr:nth-child(odd) > td { background: transparent !important; }
        body[data-theme='dark'] .gg-sayfa .ant-table-tbody > tr:hover > td { background: rgba(22, 119, 255, 0.12) !important; }
        body[data-theme='dark'] .gg-sayfa .ant-btn-dangerous:not(.ant-btn-primary) { border-color: #ff4d4f !important; color: #ff7875 !important; }
        body[data-theme='dark'] .gg-sayfa .ant-tag-error { color: #ff7875 !important; background: rgba(255, 77, 79, 0.12) !important; border-color: #a61d24 !important; }
        body[data-theme='dark'] .gg-sayfa .ant-tag-warning { color: #ffc53d !important; background: rgba(250, 173, 20, 0.12) !important; border-color: #ad6800 !important; }
        body[data-theme='dark'] .gg-sayfa .ant-tag-success { color: #95de64 !important; background: rgba(82, 196, 26, 0.1) !important; border-color: #3b6d11 !important; }
        body[data-theme='dark'] .gg-sayfa .ant-tag-processing { color: #69b1ff !important; background: rgba(22, 119, 255, 0.12) !important; border-color: #15417e !important; }
        body[data-theme='dark'] .gg-sayfa .ant-tag-purple { color: #b37feb !important; background: rgba(146, 84, 222, 0.14) !important; border-color: #51258f !important; }
        .gg-sayfa { padding: 24px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 18px; }
        .gg-ust { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
        .gg-ayarlar { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
        .gg-ayarlar .ant-tag { margin: 0; border-radius: 999px; padding: 1px 10px; }
        .gg-ozet { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
        .gg-kutu { background: var(--gg-yuzey); border: 1px solid var(--gg-kenar); border-radius: 12px; padding: 14px 16px; }
        .gg-kutu-sayi { font-size: 26px; font-weight: 700; line-height: 1.1; font-variant-numeric: tabular-nums; }
        .gg-kutu-ad { font-size: 13px; color: var(--gg-ikincil); margin-top: 4px; }
        .gg-kisi { display: grid; grid-template-columns: minmax(260px, 380px) minmax(0, 1fr); gap: 16px; align-items: start; }
        .gg-kisi-bilgi { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
        .gg-kisi-bilgi .ant-tag { margin: 0; }
        .gg-eylemler { display: flex; flex-wrap: wrap; gap: 8px; }
        .gg-kutu-baslik { font-weight: 600; margin-bottom: 10px; display: flex; align-items: center; gap: 8px; }
        @media (max-width: 900px) { .gg-ozet { grid-template-columns: repeat(2, minmax(0, 1fr)); } .gg-kisi { grid-template-columns: 1fr; } }
        @media (max-width: 640px) { .gg-sayfa { padding: 14px; } }
      `}</style>

      <div className="gg-ust">
        <div>
          <Title level={3} style={{ margin: 0 }}><SafetyCertificateOutlined /> Giriş Güvenliği</Title>
          <div className="gg-ayarlar">
            <Tag>Hareketsizlik: {ay.bosta_dakika % 60 === 0 ? `${ay.bosta_dakika / 60} saat` : `${ay.bosta_dakika} dk`}</Tag>
            <Tag>Oturum üst sınırı: {ay.sure_saat} saat</Tag>
            <Tag color={ay.kapsam === 'herkes' ? 'success' : 'warning'}>
              İki adımlı giriş: {ay.kapsam === 'herkes' ? 'herkes' : `yalnız ${ay.kapsam}`}
            </Tag>
            <Tag>Ortam: {ay.ortam}</Tag>
          </div>
        </div>
        <Space wrap>
          <Popover title="Nasıl Çalışır?" content={nasil} trigger="click" placement="bottomRight">
            <Button type="text" icon={<QuestionCircleOutlined />}>Nasıl Çalışır?</Button>
          </Popover>
          <Button icon={<ReloadOutlined />} loading={yukleniyor} onClick={yukle}>Yenile</Button>
        </Space>
      </div>

      <div className="gg-ozet">
        {ozet.map((o) => (
          <div key={o.ad} className="gg-kutu">
            <div className="gg-kutu-sayi" style={{ color: o.ton }}>{o.sayi}</div>
            <div className="gg-kutu-ad">{o.ad}</div>
          </div>
        ))}
      </div>

      <div className="gg-kutu">
        <div className="gg-kutu-baslik"><UserOutlined /> Kişi işlemleri</div>
        <div className="gg-kisi">
          <div>
            <Select showSearch allowClear placeholder="Kişi seçin (ad ya da kullanıcı adı)" style={{ width: '100%' }}
              value={secili} onChange={setSecili} optionFilterProp="label"
              filterOption={(g, o) => o.label.toLocaleLowerCase('tr').includes(g.toLocaleLowerCase('tr'))}
              options={kisiler.map((k) => ({ value: k.kullanici_no, label: `${k.ad_soyad} · ${k.kul_adi}` }))} />
            {kisi && (
              <div className="gg-kisi-bilgi">
                {kisi.telefon
                  ? <Tag icon={<MobileOutlined />}>{kisi.telefon}</Tag>
                  : <Tag color="error" icon={<MobileOutlined />}>Telefon kaydı yok — giriş yapamaz</Tag>}
                <Tag color={kisi.eposta_var ? 'default' : 'warning'} icon={<MailOutlined />}>
                  {kisi.eposta_var ? 'E-posta var' : 'E-posta yok'}
                </Tag>
                <Tag color={kisi.iki_adim ? 'success' : 'default'}>{kisi.iki_adim ? 'İki adımlı giriş' : 'Tek adımlı giriş'}</Tag>
              </div>
            )}
          </div>
          <div className="gg-eylemler">
            <Button icon={<UnlockOutlined />} disabled={!kisi} onClick={() => islem('/kilit-kaldir', { kullanici_no: secili })}>
              Kilidi kaldır
            </Button>
            <Button icon={<KeyOutlined />} disabled={!kisi} onClick={() => islem('/kod-sifirla', { kullanici_no: secili })}>
              Kodu sıfırla
            </Button>
            <Popconfirm title="Kişinin tüm açık oturumları kapatılsın mı?" okText="Kapat" cancelText="Vazgeç"
              disabled={!kisi} onConfirm={() => islem('/oturum-kapat', { kullanici_no: secili })}>
              <Button danger icon={<LogoutOutlined />} disabled={!kisi}>Oturumlarını kapat</Button>
            </Popconfirm>
            <Popconfirm title="Kişiyi kayıtlı telefonundan arayıp kimliğini teyit ettiniz mi?" okText="Evet, üret"
              cancelText="Vazgeç" disabled={!kisi} onConfirm={baglantiUret}>
              <Button icon={<MailOutlined />} disabled={!kisi}>Şifre yenileme bağlantısı</Button>
            </Popconfirm>
          </div>
        </div>
      </div>

      <Tabs activeKey={sekme} onChange={setSekme} items={[
        { key: 'kilitler', label: `Kilitler (${veri.kilitler.length})`, children: (
          <Table rowKey={(k) => `${k.tur}:${k.hedef}`} size="middle" dataSource={veri.kilitler} columns={kilitSutunlari}
            pagination={false} scroll={{ x: 720 }}
            locale={{ emptyText: <Empty description="Kilitli ya da robot doğrulaması bekleyen hesap yok." /> }} />) },
        { key: 'oturumlar', label: `Açık Oturumlar (${veri.oturumlar.length})`, children: (
          <Table rowKey="sid" size="middle" dataSource={veri.oturumlar} columns={oturumSutunlari} scroll={{ x: 860 }}
            pagination={veri.oturumlar.length > 50 ? { pageSize: 50 } : false}
            locale={{ emptyText: <Empty description="Açık oturum yok." /> }} />) },
        { key: 'kodlar', label: `Bugünkü Kodlar (${veri.kodlar.length})`, children: (
          <Table rowKey="kullanici_no" size="middle" dataSource={veri.kodlar} columns={kodSutunlari} scroll={{ x: 760 }}
            pagination={veri.kodlar.length > 50 ? { pageSize: 50 } : false}
            locale={{ emptyText: <Empty description="Bugün kod gönderilmedi." /> }} />) },
        { key: 'olaylar', label: 'Olay Kaydı', children: (
          <>
            <Input.Search allowClear placeholder="Kişi, kullanıcı adı, IP ya da olay" value={ara}
              onChange={(e) => setAra(e.target.value)} style={{ maxWidth: 360, marginBottom: 12 }} />
            <Table rowKey={(o, i) => `${o.t}-${i}`} size="small" dataSource={olaylar} columns={olaySutunlari}
              scroll={{ x: 900 }} pagination={{ pageSize: 50, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="Kayıt yok." /> }} />
          </>) },
      ]} />

      <Modal open={!!baglanti} title="Şifre yenileme bağlantısı" onCancel={() => setBaglanti(null)}
        footer={<Button type="primary" onClick={() => setBaglanti(null)}>Tamam</Button>}>
        {baglanti && (
          <>
            <p style={{ marginTop: 0 }}><b>{baglanti.ad_soyad}</b> için bağlantı hazır. {baglanti.sure_dakika} dakika geçerli ve
              tek kullanımlık; kişiye güvenli bir kanaldan iletin (bağlantıyı alan herkes şifreyi değiştirebilir).</p>
            <Space.Compact style={{ width: '100%' }}>
              <Input readOnly value={baglanti.baglanti} onFocus={(e) => e.target.select()} />
              <Button icon={<CopyOutlined />} onClick={kopyala}>Kopyala</Button>
            </Space.Compact>
            <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 13 }}>
              Kişi şifresini değiştirdikten sonra girişte yine telefonuna doğrulama kodu gider.
            </Text>
          </>
        )}
      </Modal>
    </div>
  );
}
